import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DateTime, Duration } from 'luxon';
import { SbDateTime, SbLocalDateTime } from '../../../../services/api/api-date-time';
import { XYChartModel, ChartComponent } from '@ngx-seatback/charts';
import { API, LocalizedFrameDTO, Weekday } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-airtime-data',
  templateUrl: './airtime-data.component.html',
  styleUrls: ['./airtime-data.component.scss'],
  standalone: false,
})
export class AirtimeDataComponent implements OnInit, OnDestroy {
  @ViewChild('integratedChart') iChart: ChartComponent;
  @ViewChild('chart') chart: ChartComponent;
  @Input() integratedChart: boolean;
  @Input() resolveInCom: boolean;
  @Input() hideDeviceCount: boolean = false;
  timespan: Duration;
  @Input() halfHeight: boolean;
  airTimeData: XYChartModel = { data: undefined, height: 0, loadingDone: false, title: '', valueAssignments: [] };
  airTimeChartData: any[];
  @ViewChild('chartLegend', { read: ElementRef, static: false }) chartLegendContainer: ElementRef;
  $destroy: Subject<any> = new Subject<void>();

  constructor(
    private readonly api: API,
    private readonly errorHandler: ErrorService,
    private readonly translate: TranslateService
  ) {
    this.airTimeChartData = [];
  }

  // tslint:disable-next-line:variable-name
  _chartHeight: number;

  get chartHeight() {
    return this._chartHeight;
  }

  @Input() set chartHeight(val) {
    if (val) {
      this._chartHeight = val;
    } else {
      this._chartHeight = 150;
    }
  }

  // tslint:disable-next-line:variable-name
  _inputData: any;

  get inputData() {
    return this._inputData;
  }

  @Input() set inputData(val) {
    this._inputData = val;
    if (!this.resolveInCom && this._inputData) {
      this.initChartData(this._inputData);
    }
  }

  // tslint:disable-next-line:variable-name
  _from?: SbDateTime;

  get from(): SbDateTime {
    return this._from;
  }

  @Input() set from(val: SbDateTime) {
    this._from = val;
  }

  // tslint:disable-next-line:variable-name
  _till?: SbDateTime;

  get till(): SbDateTime {
    return this._till;
  }

  @Input() set till(val: SbDateTime) {
    this._till = val;
  }

  ngOnDestroy(): void {
    this.$destroy.next(null);
    this.$destroy.complete();
  }

  ngOnInit(): void {
    if (this.resolveInCom) {
      this.refreshFromServer();
    }
    this.translate.onLangChange.pipe(takeUntil(this.$destroy)).subscribe(() => {
      this.buildChart();
    });
  }

  refreshFromServer() {
    if (!this.from && !this.till) {
      this.from = new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 91 }));
      this.till = new SbLocalDateTime(DateTime.local().startOf('day').minus({ day: 1 }));
    }
    this.api
      .ss_nm_Reporting_airtimeQuota({
        productive: true,
        interval: 604800,
        weekday: [Weekday.MONDAY, Weekday.TUESDAY, Weekday.WEDNESDAY, Weekday.THURSDAY, Weekday.FRIDAY, Weekday.SATURDAY],
        hour: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
        from: new SbLocalDateTime(this.from.value),
        till: new SbLocalDateTime(this.till.value),
      })
      .subscribe({
        next: data => {
          this.initChartData(data.buckets[0].frames);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  initChartData(data: LocalizedFrameDTO[]) {
    const fromTimespan = this.from.value;
    const tillTimespan = this.till.value;
    this.timespan = tillTimespan.diff(fromTimespan, 'days');
    this.airTimeChartData = [];

    for (const i of data) {
      this.airTimeChartData.push({
        date: i.range.from.value,
        value: Math.round(i.data.quota * 100) + '%',
        devices: Math.round(i.data.devices),
      });
    }
    this.buildChart();
  }

  buildChart() {
    const player = this.translate.instant('lang.globals.player');
    const quota = this.translate.instant('lang.dashboard.broadcastingQuota');
    if (this.hideDeviceCount) {
      this.airTimeData = {
        title: 'Test',
        height: this.chartHeight,
        valueAssignments: [
          {
            value: 'value',
            categories: ['date'],
            color: '#0194dc',
            valueName: quota,
          },
        ],
        categoryType: 'date',
        data: this.airTimeChartData,
      };
    } else {
      this.airTimeData = {
        title: 'Test',
        height: this.chartHeight,
        valueAssignments: [
          {
            value: 'value',
            categories: ['date'],
            color: '#0194dc',
            valueName: quota,
          },
          {
            value: 'devices',
            categories: ['date'],
            color: 'rgba(215,219,220,0.43)',
            valueName: player,
            ownAxis: true,
            opposite: true,
          },
        ],
        categoryType: 'date',
        data: this.airTimeChartData,
      };
    }
    this.airTimeData.loadingDone = true;
    if (this.integratedChart) {
      this.airTimeData.fill = true;
      this.airTimeData.legend = false;
      this.airTimeData.showBullet = false;
      this.airTimeData.disableAxis = true;
      this.airTimeData.disableLabel = true;
      this.airTimeData.startAtBorder = true;
      this.airTimeData.gradient = true;
      if (this.iChart) {
        this.iChart.rerenderChart(this.airTimeData);
      }
    } else {
      this.airTimeData.legend = true;
      this.airTimeData.legendContainer = this.chartLegendContainer;
      this.airTimeData.legendAlign = 'right';
      this.airTimeData.tensionX = 1;
      if (this.chart) {
        this.chart.rerenderChart(this.airTimeData);
      }
    }
  }
}
